import JSZip from "jszip";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useAppStore } from "../../hooks/useStores";
import { CampaignEntityStatus } from "../../models/common.ts";
import { IframePostMessageActionTypes, IframePostMessageItemTypes } from "../../models/new-iframe.ts";
import iframeStore from "../../pages/IframeNew/iframe-store.tsx";
import { DesignEditorContext } from "../contexts/DesignEditor.tsx";
import useDesignEditorContext from "../hooks/useDesignEditorContext";
import { useEditor } from "../hooks/useEditor";
import { IDesign } from "../interfaces/DesignEditor";
import { Template } from "../models/template";
import { IFrame, IScene } from "../types";
import { loadTemplateFonts } from "../utils/fonts";
import { loadVideoEditorAssets } from "../utils/video";
import useScenesUtils from "./ScenesUtils";

type URLDetails = {
	target: string | null;
	id: string | null;
	adsIndex: number | undefined;
	brandId: number | null;
};

type TemplateUtils = {
	loadGraphicTemplate: (payload: IDesign) => Promise<{
		scenes: IScene[];
		design: { id: string; name: string; frame: IFrame; preview: string; metadata: any };
	}>;
	loadTemplate: (template: Template, adsIndex?: number) => Promise<void>;
	handleDownloadTemplate: (template: Template) => Promise<void>;
	handleIframeSaveTemplate: (template: Template) => Promise<void>;
	exportTemplate: (l_scenes?: IScene[]) => Promise<IDesign | undefined>;
	getTemplateDetailsFromURL: () => URLDetails;
	pollForTemplate: (templateId: string, maxAttempts?: number, delay?: number) => Promise<any>;
};

const UseTemplateUtils = (): TemplateUtils => {
	const editor = useEditor();
	const { campaignStore, uiStore } = useAppStore();
	const { updateScenes } = useScenesUtils();
	const { t } = useTranslation("translation", { keyPrefix: "editor.tabs.templates" });
	const { currentScene, setScenes, setCurrentDesign, scenes, currentDesign, setCurrentScene } =
		useDesignEditorContext();
	const { isPopupView } = useContext(DesignEditorContext);

	const loadGraphicTemplate = async (payload: IDesign) => {
		if (!editor) {
			return {
				scenes: [] as IScene[],
				design: {
					id: "",
					name: "",
					frame: { width: 0, height: 0 } as IFrame,
					preview: "",
					metadata: {},
				},
			};
		}

		const { scenes: scns, ...design } = payload;
		const scenes: IScene[] = new Array(scns.length);

		const scenePromises = scns.map((scn, index) => {
			const scene: IScene = {
				name: scn.name,
				frame: {
					width: scn.layers[0].width,
					height: scn.layers[0].height,
				},
				id: scn.id,
				layers: scn.layers,
				isMainScene: scn.isMainScene,
				metadata: {},
			};

			return loadVideoEditorAssets(scene)
				.then((loadedScene) => loadTemplateFonts(loadedScene).then(() => loadedScene))
				.then((loadedScene) =>
					editor.renderer.render(loadedScene).then((preview: string) => {
						scenes[index] = { ...loadedScene, preview };
					}),
				);
		});

		return Promise.all(scenePromises).then(() => ({
			scenes: scenes.filter((scene) => scene !== null),
			design,
		}));
	};

	const handleImportTemplate = React.useCallback(
		(data: IDesign, adsIndex?: number) => {
			return new Promise<any>((resolve, reject) => {
				loadGraphicTemplate(data)
					.then((template) => {
						if (template) {
							let defaultLoadedScene =
								adsIndex && adsIndex >= 1 ? template.scenes[adsIndex - 1] : template.scenes[0];
							if (isPopupView) {
								const mainScene = template.scenes.find((scene) => scene.isMainScene);
								if (mainScene) {
									defaultLoadedScene = mainScene;
								}
							}
							setScenes(template.scenes as IScene[]);
							setCurrentScene(defaultLoadedScene);
							setCurrentDesign(template.design as IDesign);
							resolve(template);
						} else {
							reject(new Error("Template is undefined"));
						}
					})
					.catch((error) => {
						console.error("Error importing template:", error);
						reject(error);
					});
			});
		},
		[editor],
	);

	function base64toBlob(base64Data: string, contentType: string) {
		const sliceSize = 1024;
		const base64WithoutPrefix = base64Data.split(",")[1];
		const byteCharacters = atob(base64WithoutPrefix);
		const byteArrays = [];

		for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
			const slice = byteCharacters.slice(offset, offset + sliceSize);
			const byteNumbers = new Array(slice.length);
			for (let i = 0; i < slice.length; i++) {
				byteNumbers[i] = slice.charCodeAt(i);
			}
			const byteArray = new Uint8Array(byteNumbers);
			byteArrays.push(byteArray);
		}

		return new Blob(byteArrays, { type: contentType });
	}

	const prepareTemplateForExport = async (template: Template) => {
		let storedScenes;
		if (campaignStore.selectedTemplate && campaignStore?.selectedTemplate?.id == template.id) {
			const scenes = await updateScenes();
			storedScenes = scenes.map((scene) => ({
				...scene,
				preview: scene.preview ?? "",
			}));
		} else {
			const templateToImport = await loadGraphicTemplate(template.ads_json);
			if (templateToImport) {
				storedScenes = templateToImport.scenes.map((scene) => ({
					...scene,
					preview: scene?.preview ?? "",
				}));
			}
		}
		return storedScenes;
	};

	const handleDownloadTemplate = async (template: Template) => {
		const zip = new JSZip();
		const storedScenes = await prepareTemplateForExport(template);

		const imagesBase64 = [];
		let i = 0;
		for (const scene of storedScenes ?? []) {
			i += 1;
			const imageData = await editor?.renderer.render(scene as IScene);
			imagesBase64.push(imageData);
			const blob = base64toBlob(imageData ?? "", "image/png");
			const imageName = scene?.name ? `${scene.name + i}.png` : `scene${i}.png`;
			zip.file(imageName, blob);
		}

		const metadataObj = {
			id: template.id,
			name: template.name,
		};
		const items = imagesBase64.map((base64, index) => ({
			type: IframePostMessageItemTypes.Image,
			src: base64 ?? "",
			fileName: storedScenes?.[index]?.name
				? `${storedScenes[index].name}${index + 1}.png`
				: `scene${index + 1}.png`,
		}));

		iframeStore.sendExportPostMessage(
			IframePostMessageActionTypes.Download,
			IframePostMessageItemTypes.Template,
			items,
			{
				template: metadataObj,
			},
		);

		const zipContent = await zip.generateAsync({ type: "blob" });
		const zipUrl = URL.createObjectURL(zipContent);
		const link = document.createElement("a");
		link.href = zipUrl;
		link.download = "scenes.zip";
		document.body.appendChild(link);
		link.click();

		// Clean up
		URL.revokeObjectURL(zipUrl);
		document.body.removeChild(link);
	};

	const handleIframeSaveTemplate = async (template: Template) => {
		const storedScenes = await prepareTemplateForExport(template);

		const imagesBase64 = [];
		for (const scene of storedScenes ?? []) {
			const imageData = await editor?.renderer.render(scene as IScene);
			imagesBase64.push(imageData);
		}

		const metadataObj = {
			id: template.id,
			name: template.name,
		};
		const items = imagesBase64.map((base64, index) => ({
			type: IframePostMessageItemTypes.Image,
			src: base64 ?? "",
			fileName: storedScenes?.[index]?.name
				? `${storedScenes[index].name}${index + 1}.png`
				: `scene${index + 1}.png`,
		}));

		iframeStore.sendExportPostMessage(
			IframePostMessageActionTypes.Save,
			IframePostMessageItemTypes.Template,
			items,
			{
				template: metadataObj,
			},
		);
	};

	const loadTemplate = React.useCallback(
		(template: any, adsIndex?: number) => {
			return new Promise<any>((resolve, reject) => {
				if (editor) {
					handleImportTemplate(template, adsIndex)
						.then((template) => {
							editor?.canvas.canvas.requestRenderAll();
							resolve(template);
						})
						.catch((error) => {
							console.error("Error loading template: ", error);
							reject(error);
						});
				} else {
					reject(new Error("Editor not initialized"));
				}
			});
		},
		[editor, currentScene],
	);

	const exportTemplate = async (l_scenes?: IScene[]) => {
		if (!l_scenes) {
			l_scenes = scenes;
		}

		const currentScene = editor && editor.scene.exportToJSON();
		if (currentScene) {
			const updatedScenes = l_scenes.map((scene) => {
				if (scene.id === currentScene.id) {
					return {
						id: currentScene.id,
						layers: currentScene.layers,
						name: currentScene.name,
						isMainScene: scene.isMainScene,
					};
				}
				return {
					id: scene.id,
					layers: scene.layers,
					name: scene.name,
					isMainScene: scene.isMainScene,
				};
			});
			if (currentDesign) {
				const graphicTemplate: IDesign = {
					id: currentDesign.id,
					name: currentDesign.name,
					frame: currentDesign.frame,
					scenes: updatedScenes,
					metadata: {},
					preview: "",
				};
				return graphicTemplate;
			} else {
				console.log(t("NoCurrentdesign"));
				return undefined;
			}
		}
	};
	const getTemplateDetailsFromURL = () => {
		const pathParts = location.pathname.split("/");
		const fullId = pathParts[pathParts.length - 1];

		if (!fullId) {
			return { target: null, id: null, adsIndex: undefined, brandId: null };
		}

		// Parse path ID
		const target = fullId.charAt(0);
		const id = fullId.substring(1);

		const params = new URLSearchParams(location.search);
		const adsIndex = params.get("adsIndex");
		const brandId = params.get("brandId");

		return {
			target,
			id,
			adsIndex: adsIndex ? Number(adsIndex) : undefined,
			brandId: brandId ? Number(brandId) : null,
		};
	};

	const pollForTemplate = async (templateId: string, maxAttempts = 50, delay = 6000) => {
		for (let attempt = 1; attempt <= maxAttempts; attempt++) {
			const template = await campaignStore.getTemplate(templateId);
			if (template && template.status === CampaignEntityStatus.DRAFT) return template;

			console.log(`Template not ready yet (attempt ${attempt}/${maxAttempts})...`);
			await new Promise((res) => setTimeout(res, delay));
		}
		uiStore.iframeAdsEditorInfoPopup = false;
		throw new Error("Template not available after polling attempts");
	};

	return {
		loadGraphicTemplate,
		loadTemplate,
		handleDownloadTemplate,
		handleIframeSaveTemplate,
		exportTemplate,
		getTemplateDetailsFromURL,
		pollForTemplate,
	};
};

export default UseTemplateUtils;
